import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import {
  FileText, Search, Loader2, ChevronLeft, ChevronRight, Eye, Mail, CheckCircle2, XCircle, Trash2, FileDown,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Textarea } from "./ui/textarea";
import { DateInput } from "./ui/date-input";

const fmt = (d) => {
  if (!d) return "—";
  const s = String(d);
  if (s.includes("T")) {
    try {
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) {
        return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
      }
    } catch (_) { /* noop */ }
    return s;
  }
  const parts = s.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return s;
};

const PretrialActsTab = () => {
  const [filters, setFilters] = useState({
    kind: "all",
    court_type: "all",
    act_number: "",
    subject_contains: "",
    generated_from: "",
    generated_to: "",
  });
  const [data, setData] = useState({ acts: [], total: 0, page: 1, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filters.kind !== "all") params.kind = filters.kind;
      if (filters.court_type !== "all") params.court_type = filters.court_type;
      if (filters.act_number) params.act_number = filters.act_number;
      if (filters.subject_contains) params.subject_contains = filters.subject_contains;
      if (filters.generated_from) params.generated_from = filters.generated_from;
      if (filters.generated_to) params.generated_to = filters.generated_to;
      const r = await axios.get(`${API}/pretrial-tools/all-acts`, { params });
      setData(r.data || { acts: [], total: 0, page: 1, total_pages: 1 });
    } catch (e) {
      toast.error("Σφάλμα φόρτωσης πράξεων");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetFilters = () => {
    setFilters({ kind: "all", court_type: "all", act_number: "", subject_contains: "", generated_from: "", generated_to: "" });
    setPage(1);
  };

  // Cancellation
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Hard delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const submitCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const r = await axios.post(`${API}/pretrial-tools/cancel-assignment`, {
        case_id: cancelTarget.case_id,
        reason: cancelReason.trim(),
      });
      toast.success(`Ακυρώθηκε ο ορισμός — Πράξη ${r.data?.previous_act || ""}`);
      setCancelTarget(null);
      setCancelReason("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ακύρωσης");
    } finally {
      setCancelling(false);
    }
  };

  const submitHardDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await axios.delete(`${API}/pretrial-tools/judge-assignment-history/${deleteTarget.case_id}`);
      toast.success(`Διαγράφηκε οριστικά: Πράξη ${r.data?.previous_act || ""}`);
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα διαγραφής");
    } finally {
      setDeleting(false);
    }
  };

  // Κατέβασμα Πράξης απευθείας από την καρτέλα Πράξεις Προδικαστικού
  const downloadAct = async (a, format = "docx") => {
    if (!a?.case_id) return;
    try {
      const endpoint = a.kind === "rapporteur"
        ? `${API}/pretrial-sectors/generate-rapporteur-act/${a.case_id}`
        : `${API}/pretrial-sectors/generate-act/${a.case_id}`;
      const params = new URLSearchParams({ format });
      if (a.act_number) params.append("act_number", String(a.act_number));
      const res = await axios.get(`${endpoint}?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = m ? m[1] : `praxi_${a.act_number}_${a.act_year}.${format}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Πράξη ${a.act_number}/${a.act_year} (${format.toUpperCase()}) έτοιμη`);
    } catch (e) {
      let msg = "Σφάλμα κατεβάσματος";
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* ignore */ }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Πράξεις Προδικαστικού Ελέγχου</CardTitle>
          <CardDescription>
            Όλες οι παραγμένες Πράξεις Ορισμού (Δικαστή/Σύνθεσης) και Πράξεις Ορισμού Εισηγητή. Αναζήτηση κατά αριθμό, αντικείμενο ή ημερομηνία παραγωγής.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Τύπος Πράξης</Label>
              <Select value={filters.kind} onValueChange={v => { setFilters(f => ({ ...f, kind: v })); setPage(1); }}>
                <SelectTrigger data-testid="pa-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες</SelectItem>
                  <SelectItem value="assignment">Ορισμός Δικαστή/Σύνθεσης</SelectItem>
                  <SelectItem value="rapporteur">Ορισμός Εισηγητή</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Δικαστήριο</Label>
              <Select value={filters.court_type} onValueChange={v => { setFilters(f => ({ ...f, court_type: v })); setPage(1); }}>
                <SelectTrigger data-testid="pa-court"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλα</SelectItem>
                  <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                  <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Αρ. Πράξης (περιέχει)</Label>
              <Input value={filters.act_number} onChange={e => { setFilters(f => ({ ...f, act_number: e.target.value })); setPage(1); }} placeholder="π.χ. 99" data-testid="pa-num" />
            </div>
            <div>
              <Label className="text-xs">Αντικείμενο (περιέχει)</Label>
              <Input value={filters.subject_contains} onChange={e => { setFilters(f => ({ ...f, subject_contains: e.target.value })); setPage(1); }} placeholder="π.χ. ΕΝΟΧΙΚΟ" data-testid="pa-subj" />
            </div>
            <div>
              <Label className="text-xs">Παραγωγή Από</Label>
              <DateInput value={filters.generated_from} onChange={e => { setFilters(f => ({ ...f, generated_from: e.target.value })); setPage(1); }} data-testid="pa-from" />
            </div>
            <div>
              <Label className="text-xs">Παραγωγή Έως</Label>
              <DateInput value={filters.generated_to} onChange={e => { setFilters(f => ({ ...f, generated_to: e.target.value })); setPage(1); }} data-testid="pa-to" />
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="text-sm text-slate-500">
              {loading ? "Φόρτωση..." : <>Σύνολο: <strong>{data.total}</strong> πράξεις</>}
            </div>
            <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="pa-reset">Καθαρισμός φίλτρων</Button>
          </div>

          <div className="overflow-x-auto border rounded">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Τύπος</th>
                  <th className="p-2 text-left">Αρ. Πράξης</th>
                  <th className="p-2 text-left">ΓΑΚ</th>
                  <th className="p-2 text-left">ΕΑΚ</th>
                  <th className="p-2 text-left">Δικαστήριο</th>
                  <th className="p-2 text-left">Αντικείμενο</th>
                  <th className="p-2 text-left">Εισηγητής</th>
                  <th className="p-2 text-center">Κατάθεση</th>
                  <th className="p-2 text-center">Παραγωγή</th>
                  <th className="p-2 text-center">Email</th>
                  <th className="p-2 text-center"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 inline animate-spin" /></td></tr>
                ) : data.acts.length === 0 ? (
                  <tr><td colSpan={12} className="p-6 text-center text-slate-400 italic">Δεν βρέθηκαν πράξεις</td></tr>
                ) : data.acts.map((a, idx) => (
                  <tr key={`${a.case_id}-${a.kind}-${idx}`} className="border-t hover:bg-slate-50" data-testid={`pa-row-${idx}`}>
                    <td className="p-2 text-slate-400">{(page - 1) * 50 + idx + 1}</td>
                    <td className="p-2">
                      <Badge variant="outline" className={`text-[10px] ${a.kind === "rapporteur" ? "border-purple-300 text-purple-700 bg-purple-50" : "border-blue-300 text-blue-700 bg-blue-50"}`}>
                        {a.kind_label}
                      </Badge>
                    </td>
                    <td className="p-2 font-mono text-xs">
                      <Badge className="bg-emerald-600 text-white">{a.act_number}/{a.act_year}</Badge>
                    </td>
                    <td className="p-2 font-mono text-xs">{a.gak}</td>
                    <td className="p-2 font-mono text-xs text-blue-700">{a.eak}</td>
                    <td className="p-2 text-xs">{a.court_type}</td>
                    <td className="p-2 text-xs max-w-[200px] truncate" title={a.subject}>{a.subject || "—"}</td>
                    <td className="p-2 text-xs">{a.rapporteur_name || <span className="text-slate-300">—</span>}</td>
                    <td className="p-2 text-center font-mono text-xs">{fmt(a.filing_date)}</td>
                    <td className="p-2 text-center font-mono text-xs">{fmt(a.generated_at)}</td>
                    <td className="p-2 text-center">
                      {a.emailed_at ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" title={fmt(a.emailed_at)} /> : <Mail className="w-4 h-4 text-slate-300 inline" />}
                    </td>
                    <td className="p-2 text-center">
                      <Link to={`/cases/${a.case_id}`}>
                        <Button size="sm" variant="ghost" className="h-7 px-2" title="Άνοιγμα υπόθεσης" data-testid={`pa-open-${idx}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </Link>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => downloadAct(a, "docx")}
                        data-testid={`pa-dl-docx-${idx}`}
                        title="Κατέβασμα Πράξης (DOCX)"
                      >
                        <FileDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 px-2 text-blue-700 hover:bg-blue-50"
                        onClick={() => downloadAct(a, "pdf")}
                        data-testid={`pa-dl-pdf-${idx}`}
                        title="Κατέβασμα Πράξης (PDF)"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => { setCancelTarget(a); setCancelReason(""); }}
                        data-testid={`pa-cancel-${idx}`}
                        title="Ακύρωση Ορισμού & Πράξης"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 px-2 text-red-800 hover:bg-red-100 hover:text-red-900"
                        onClick={() => setDeleteTarget(a)}
                        data-testid={`pa-delete-${idx}`}
                        title="ΠΛΗΡΗΣ ΔΙΑΓΡΑΦΗ — οριστική, χωρίς audit"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Σελίδα {data.page} / {data.total_pages}</span>
              <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Assignment Confirmation Dialog */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <XCircle className="w-5 h-5" /> Ακύρωση Πράξης Ορισμού
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Πρόκειται να ακυρώσετε την Πράξη Ορισμού:</p>
                <div className="bg-slate-50 border rounded p-2 font-mono text-xs">
                  Πράξη {cancelTarget?.act_number}/{cancelTarget?.act_year} ·
                  ΓΑΚ {cancelTarget?.gak} · Δικαστής: <b>{cancelTarget?.rapporteur_name || "—"}</b>
                </div>
                <p className="text-red-600 text-xs">
                  Η υπόθεση επανέρχεται σε κατάσταση <b>προ-ορισμού</b>. Η ενέργεια καταγράφεται σε audit log.
                </p>
                <div>
                  <Label className="text-xs">Αιτιολογία (προαιρετική):</Label>
                  <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                            rows={3} className="mt-1 text-sm" data-testid="pa-cancel-reason" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Άκυρο</AlertDialogCancel>
            <AlertDialogAction onClick={submitCancel} disabled={cancelling}
                               className="bg-red-600 hover:bg-red-700" data-testid="pa-confirm-cancel">
              {cancelling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
              Ακύρωση Ορισμού
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Hard Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-800 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> ΠΛΗΡΗΣ ΔΙΑΓΡΑΦΗ Πράξης
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Πρόκειται να διαγράψετε <b>ΟΡΙΣΤΙΚΑ</b> την Πράξη Ορισμού:</p>
                <div className="bg-red-50 border border-red-200 rounded p-2 font-mono text-xs">
                  Πράξη {deleteTarget?.act_number}/{deleteTarget?.act_year} ·
                  ΓΑΚ {deleteTarget?.gak} · Δικαστής: <b>{deleteTarget?.rapporteur_name || "—"}</b>
                </div>
                <p className="text-red-700 text-xs font-semibold">
                  ⚠ ΧΩΡΙΣ καταγραφή σε audit log. Δεν θα μπορείτε να ανακτήσετε αυτή τη πληροφορία.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Άκυρο</AlertDialogCancel>
            <AlertDialogAction onClick={submitHardDelete} disabled={deleting}
                               className="bg-red-800 hover:bg-red-900" data-testid="pa-confirm-delete">
              {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Οριστική Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PretrialActsTab;
